import type { WorkOrderListItemView, WorkOrderListQuery } from '@chukua/common';
import { WORK_ORDER_SEVERITIES, WORK_ORDER_SOURCES, WORK_ORDER_STATES } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link } from 'react-router';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { formatMoney } from '../../../../../shared/policies/format-money';
import {
  WORK_ORDER_SEVERITY_COLORS,
  WORK_ORDER_SEVERITY_LABELS,
  WORK_ORDER_SOURCE_LABELS,
  WORK_ORDER_STATE_COLORS,
  WORK_ORDER_STATE_LABELS,
  workOrderNumber,
} from '../policies/work-order-labels';
import { workOrderPath } from '../work-orders.paths';

interface BuildWorkOrderColumnsArgs {
  list: Pick<ListQueryControls<WorkOrderListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  registrations: ReadonlyMap<string, string>;
}

/** A column's `key` is its sort field or filter param in `workOrderListQuerySchema`; the truck filter is a search box in the toolbar. */
export function buildWorkOrderColumns({ list, registrations }: BuildWorkOrderColumnsArgs): TableColumnsType<WorkOrderListItemView> {
  return [
    {
      title: 'Order',
      dataIndex: 'sequenceNumber',
      key: 'sequenceNumber',
      sorter: true,
      sortOrder: list.sortOrderFor('sequenceNumber'),
      render: (number: number, row) => <Link to={workOrderPath(row.id)}>{workOrderNumber(number)}</Link>,
    },
    { title: 'Truck', key: 'truckId', render: (_, row) => registrations.get(row.truckId) ?? '—' },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      sorter: true,
      sortOrder: list.sortOrderFor('state'),
      filters: WORK_ORDER_STATES.map((state) => ({ text: WORK_ORDER_STATE_LABELS[state], value: state })),
      filteredValue: list.filteredValueFor('state'),
      render: (state: WorkOrderListItemView['state']) => <Tag color={WORK_ORDER_STATE_COLORS[state]}>{WORK_ORDER_STATE_LABELS[state]}</Tag>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      sorter: true,
      sortOrder: list.sortOrderFor('severity'),
      filters: WORK_ORDER_SEVERITIES.map((severity) => ({ text: WORK_ORDER_SEVERITY_LABELS[severity], value: severity })),
      filteredValue: list.filteredValueFor('severity'),
      render: (severity: WorkOrderListItemView['severity']) => <Tag color={WORK_ORDER_SEVERITY_COLORS[severity]}>{WORK_ORDER_SEVERITY_LABELS[severity]}</Tag>,
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      filters: WORK_ORDER_SOURCES.map((source) => ({ text: WORK_ORDER_SOURCE_LABELS[source], value: source })),
      filteredValue: list.filteredValueFor('source'),
      render: (source: WorkOrderListItemView['source']) => WORK_ORDER_SOURCE_LABELS[source],
    },
    {
      title: 'Estimate',
      dataIndex: 'estimatedCost',
      key: 'estimatedCost',
      sorter: true,
      sortOrder: list.sortOrderFor('estimatedCost'),
      render: formatMoney,
    },
    {
      title: 'Raised',
      dataIndex: 'raisedAt',
      key: 'raisedAt',
      sorter: true,
      sortOrder: list.sortOrderFor('raisedAt'),
      render: (raisedAt: string) => new Date(raisedAt).toLocaleDateString('en-KE'),
    },
  ];
}
